import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, X, AlertCircle, Loader2 } from "lucide-react";
import {
  getCourse,
  getEnrollment,
  updateEnrollment,
  completeCourse,
  submitSection,
  listCourses,
} from "../../services/courseService";
import { useGlobalState } from "../../GlobalStates";
import RichTextViewer from "../../components/common/RichTextViewer";
import juniorDuck from "../../assets/images/juniorduckstatic.png";

const LOCALE_TO_LANGUAGE = {
  en: "en",
  "zh-Hant-TW": "zh-Hant-TW",
  "zh-Hans-CN": "zh-Hans-CN",
  ja: "ja",
  ko: "ko",
  th: "th",
  vi: "vi",
};

const GRADED_TYPES = ["single_select", "multi_select", "true_false"];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SectionIntro = ({ title, introText }) => (
  <div className="tw-w-full tw-max-w-2xl tw-space-y-6">
    <div className="tw-space-y-2">
      <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">{title}</h3>
      <RichTextViewer content={introText} className="tw-text-gray-600" />
    </div>
  </div>
);

const ValidationWarning = ({ message }) => (
  <motion.div
    initial={{ opacity: 0, y: 4 }}
    animate={{ opacity: 1, y: 0 }}
    className="tw-mt-4 tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-700"
  >
    <AlertCircle className="tw-h-4 tw-w-4 tw-flex-shrink-0" />
    {message}
  </motion.div>
);

const QuizQuestion = ({
  questions,
  selectedAnswers,
  onSelect,
  startingNumber = 1,
  validationErrors,
}) => (
  <div className="tw-w-full tw-max-w-2xl tw-space-y-8 tw-pb-8 md:tw-space-y-12 md:tw-pb-12">
    {questions.map((question, qIndex) => {
      const hasError = validationErrors?.includes(question.id);
      return (
        <div key={question.id} className="tw-space-y-6">
          <div className="tw-space-y-2">
            <div
              className={`tw-text-lg tw-font-semibold md:tw-text-xl ${hasError ? "tw-text-red-600" : "tw-text-gray-900"}`}
            >
              <span>{startingNumber + qIndex}. </span>
              <RichTextViewer
                content={question.question_text}
                className="tw-inline [&_p]:tw-my-0 [&_p]:tw-inline"
              />
            </div>
            {question.question_type === "multi_select" && (
              <p className="tw-text-sm tw-text-gray-500">Select all that apply</p>
            )}
            {hasError && <p className="tw-text-sm tw-text-red-500">This question is required</p>}
          </div>

          {/* single_select / true_false / multi_select: option buttons */}
          {["single_select", "multi_select", "true_false"].includes(question.question_type) && (
            <div className="tw-space-y-3">
              {(question.options || []).map((option) => {
                const isMulti = question.question_type === "multi_select";
                const isSelected = isMulti
                  ? (selectedAnswers[question.id] || []).includes(option.id)
                  : selectedAnswers[question.id] === option.id;

                return (
                  <motion.button
                    key={option.id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => {
                      if (isMulti) {
                        const curr = selectedAnswers[question.id] || [];
                        const next = isSelected
                          ? curr.filter((id) => id !== option.id)
                          : [...curr, option.id];
                        onSelect(question.id, next);
                      } else {
                        onSelect(question.id, option.id);
                      }
                    }}
                    className={`tw-group tw-w-full tw-rounded-xl tw-p-4 tw-text-left tw-transition-all tw-duration-200 md:tw-p-5 ${
                      isSelected
                        ? "tw-bg-primary-main tw-text-white"
                        : `tw-bg-gray-50 hover:tw-bg-gray-100 ${hasError ? "tw-ring-1 tw-ring-red-200" : ""}`
                    }`}
                  >
                    <div className="tw-flex tw-items-center tw-gap-3">
                      {isSelected ? (
                        <div
                          className={`tw-flex tw-h-6 tw-w-6 tw-flex-none tw-items-center tw-justify-center tw-bg-white ${isMulti ? "tw-rounded-md" : "tw-rounded-full"}`}
                        >
                          <Check className="tw-h-4 tw-w-4 tw-text-primary-main" />
                        </div>
                      ) : (
                        <div
                          className={`tw-h-6 tw-w-6 tw-flex-none tw-border-2 tw-bg-white ${isMulti ? "tw-rounded-md" : "tw-rounded-full"}`}
                        />
                      )}
                      <p className="tw-text-base tw-font-medium">{option.text}</p>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          )}

          {/* open_ended: textarea */}
          {question.question_type === "open_ended" && (
            <textarea
              value={selectedAnswers[question.id] || ""}
              onChange={(e) => onSelect(question.id, e.target.value)}
              rows={4}
              placeholder="Type your answer..."
              className={`tw-w-full tw-rounded-xl tw-border tw-bg-gray-50 tw-p-4 tw-text-sm tw-leading-relaxed focus:tw-border-primary-main focus:tw-outline-none ${
                hasError ? "tw-border-red-300" : "tw-border-gray-200"
              }`}
            />
          )}

        </div>
      );
    })}
  </div>
);

/** Separate page showing open-ended questions with AI feedback */
const ModelAnswersView = ({ questions, answers, startingNumber = 1, sectionResults = [] }) => {
  const feedbackMap = useMemo(() => {
    const map = {};
    (sectionResults || []).forEach((r) => {
      if (r.feedback) map[r.questionId] = r.feedback;
    });
    return map;
  }, [sectionResults]);

  const openEnded = questions.filter((q) => q.question_type === "open_ended");
  if (openEnded.length === 0) return null;

  return (
    <div className="tw-w-full tw-max-w-2xl tw-space-y-8 tw-pb-8 md:tw-space-y-10 md:tw-pb-12">
      <div className="tw-rounded-xl tw-bg-blue-50 tw-p-4 tw-text-center md:tw-p-5">
        <h3 className="tw-text-lg tw-font-semibold tw-text-blue-900">Review</h3>
        <p className="tw-mt-1 tw-text-sm tw-text-blue-700">
          Review the feedback on your answers below.
        </p>
      </div>

      {openEnded.map((question) => {
        const qIndex = questions.indexOf(question);
        const feedback = feedbackMap[question.id];

        return (
          <div key={question.id} className="tw-space-y-4">
            <div className="tw-text-lg tw-font-semibold tw-text-gray-900">
              <span>{startingNumber + qIndex}. </span>
              <RichTextViewer
                content={question.question_text}
                className="tw-inline [&_p]:tw-my-0 [&_p]:tw-inline"
              />
            </div>

            <div>
              <p className="tw-mb-1.5 tw-text-xs tw-font-medium tw-text-gray-500">Your answer</p>
              <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4">
                <p className="tw-whitespace-pre-wrap tw-text-sm tw-text-gray-700">
                  {answers[question.id] || ""}
                </p>
              </div>
            </div>

            {feedback ? (
              <div>
                <p className="tw-mb-1.5 tw-text-xs tw-font-medium tw-text-blue-600">Feedback</p>
                <div className="tw-rounded-xl tw-border tw-border-blue-200 tw-bg-blue-50 tw-p-4">
                  <p className="tw-whitespace-pre-wrap tw-text-sm tw-text-blue-800">{feedback}</p>
                </div>
              </div>
            ) : question.model_answer ? (
              <div>
                <p className="tw-mb-1.5 tw-text-xs tw-font-medium tw-text-green-600">
                  Model answer
                </p>
                <div className="tw-rounded-xl tw-border tw-border-green-200 tw-bg-green-50 tw-p-4">
                  <RichTextViewer
                    content={question.model_answer}
                    className="tw-text-sm tw-text-green-800"
                  />
                </div>
              </div>
            ) : (
              <div>
                <p className="tw-mb-1.5 tw-text-xs tw-font-medium tw-text-gray-400">
                  Feedback is being generated...
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Quiz-style feedback view showing results inline on each question */
const QuizFeedback = ({ questions, answers, sectionResults, startingNumber = 1, onRetake }) => {
  const resultMap = useMemo(() => {
    const map = {};
    sectionResults.forEach((r) => {
      map[r.questionId] = r;
    });
    return map;
  }, [sectionResults]);

  const totalGraded = sectionResults.filter((r) => r.graded).length;
  const correctCount = sectionResults.filter((r) => r.graded && r.isCorrect).length;
  const passed = totalGraded === 0 || correctCount === totalGraded;

  return (
    <div className="tw-w-full tw-max-w-2xl tw-space-y-8 tw-pb-8 md:tw-space-y-12 md:tw-pb-12">
      {/* Score summary */}
      <div className="tw-rounded-xl tw-bg-gray-50 tw-p-5 tw-text-center md:tw-p-6">
        <h3 className="tw-text-xl tw-font-bold md:tw-text-2xl">
          {passed ? "Section passed!" : "Not quite yet"}
        </h3>
        <p className="tw-mt-2 tw-text-gray-500">
          You got {correctCount} out of {totalGraded} graded questions correct
        </p>
        <div className="tw-mt-4 tw-h-2 tw-rounded-full tw-bg-gray-200">
          <div
            className={`tw-h-full tw-rounded-full ${passed ? "tw-bg-green-500" : "tw-bg-yellow-500"}`}
            style={{ width: `${totalGraded > 0 ? (correctCount / totalGraded) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Questions with inline feedback (hide open_ended — shown on separate feedback page) */}
      {questions
        .filter((q) => q.question_type !== "open_ended")
        .map((question, qIndex) => {
          const globalIndex = questions.indexOf(question);
          const result = resultMap[question.id];
          const userAnswer = answers[question.id];
          const correctAnswers =
            result?.correctAnswers ||
            (result?.isCorrect && result?.graded
              ? Array.isArray(userAnswer)
                ? userAnswer
                : [userAnswer]
              : []);

          return (
            <div key={question.id} className="tw-space-y-6">
              <div className="tw-space-y-2">
                <div className="tw-flex tw-items-start tw-gap-3">
                  {result && (
                    <div
                      className={`tw-mt-1.5 tw-flex-shrink-0 tw-rounded-full tw-p-1 ${
                        !result.graded
                          ? "tw-bg-blue-100"
                          : result.isCorrect
                            ? "tw-bg-green-100"
                            : "tw-bg-red-100"
                      }`}
                    >
                      {result.graded && !result.isCorrect ? (
                        <X className="tw-h-4 tw-w-4 tw-text-red-500" />
                      ) : (
                        <Check
                          className={`tw-h-4 tw-w-4 ${result?.graded ? "tw-text-green-500" : "tw-text-blue-500"}`}
                        />
                      )}
                    </div>
                  )}
                  <div>
                    <div className="tw-text-lg tw-font-semibold tw-text-gray-900 md:tw-text-xl">
                      <span>{startingNumber + globalIndex}. </span>
                      <RichTextViewer
                        content={question.question_text}
                        className="tw-inline [&_p]:tw-my-0 [&_p]:tw-inline"
                      />
                    </div>
                    {question.question_type === "multi_select" && (
                      <p className="tw-text-sm tw-text-gray-500">Select all that apply</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Options with correct/incorrect feedback */}
              {["single_select", "multi_select", "true_false"].includes(question.question_type) && (
                <div className="tw-space-y-3">
                  {(question.options || []).map((option) => {
                    const isMulti = question.question_type === "multi_select";
                    const isSelected = isMulti
                      ? (userAnswer || []).includes(option.id)
                      : userAnswer === option.id;
                    const isCorrectOption = correctAnswers.includes(option.id);

                    let bgClass, textClass;
                    if (isCorrectOption && isSelected) {
                      bgClass = "tw-bg-green-100 tw-ring-1 tw-ring-green-300";
                      textClass = "tw-text-green-800";
                    } else if (isCorrectOption && !isSelected) {
                      bgClass = "tw-bg-green-50 tw-ring-1 tw-ring-green-200";
                      textClass = "tw-text-green-700";
                    } else if (!isCorrectOption && isSelected) {
                      bgClass = "tw-bg-red-100 tw-ring-1 tw-ring-red-300";
                      textClass = "tw-text-red-800";
                    } else {
                      bgClass = "tw-bg-gray-50";
                      textClass = "tw-text-gray-500";
                    }

                    return (
                      <div
                        key={option.id}
                        className={`tw-w-full tw-rounded-xl tw-p-4 md:tw-p-5 ${bgClass}`}
                      >
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div
                            className={`tw-flex tw-h-6 tw-w-6 tw-flex-none tw-items-center tw-justify-center ${isMulti ? "tw-rounded-md" : "tw-rounded-full"}`}
                          >
                            {isCorrectOption ? (
                              <Check className="tw-h-4 tw-w-4 tw-text-green-600" />
                            ) : isSelected ? (
                              <X className="tw-h-4 tw-w-4 tw-text-red-600" />
                            ) : (
                              <div
                                className={`tw-h-6 tw-w-6 ${isMulti ? "tw-rounded-md" : "tw-rounded-full"} tw-border-2 tw-border-gray-300`}
                              />
                            )}
                          </div>
                          <p className={`tw-text-base tw-font-medium ${textClass}`}>
                            {option.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* open_ended feedback */}
              {question.question_type === "open_ended" && (
                <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4">
                  <p className="tw-whitespace-pre-wrap tw-text-sm tw-text-gray-700">
                    {userAnswer || ""}
                  </p>
                  <p className="tw-mt-2 tw-text-xs tw-font-medium tw-text-blue-500">Submitted</p>
                </div>
              )}

            </div>
          );
        })}

      {/* Retake button */}
      {!passed && (
        <div className="tw-flex tw-flex-col tw-gap-4 tw-pt-4">
          <button
            onClick={onRetake}
            className="tw-rounded-xl tw-bg-primary-main tw-px-6 tw-py-3 tw-font-medium tw-text-white tw-transition-all hover:tw-bg-primary-main/90"
          >
            Retake this section
          </button>
          <p className="tw-text-center tw-text-sm tw-text-gray-500">
            You need 100% on graded questions to pass this section.
          </p>
        </div>
      )}
    </div>
  );
};

const SuccessView = ({ conclusionTitle, conclusionText }) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // If conclusionText exists, it already contains full congratulations content —
  // skip the extra checkmark and title to avoid duplication.
  if (conclusionText) {
    return (
      <div className="tw-w-full tw-max-w-2xl tw-space-y-8 tw-text-center">
        <AnimatePresence>
          {showContent && (
            <motion.div
              className="tw-space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <RichTextViewer content={conclusionText} className="tw-text-gray-600" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-max-w-2xl tw-space-y-8 tw-text-center">
      <motion.div
        className="tw-flex tw-justify-center"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, type: "spring", bounce: 0.4 }}
      >
        <div className="tw-flex tw-h-24 tw-w-24 tw-items-center tw-justify-center tw-rounded-full tw-bg-green-100">
          <Check className="tw-h-12 tw-w-12 tw-text-green-600" />
        </div>
      </motion.div>

      <AnimatePresence>
        {showContent && (
          <motion.div
            className="tw-space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="tw-bg-gradient-to-r tw-from-primary-main tw-to-yellow-500 tw-bg-clip-text tw-text-3xl tw-font-bold tw-text-transparent">
              {conclusionTitle || "Course Completed!"}
            </h2>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CourseTaker = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [user] = useGlobalState("user");
  const isInApp = window.isInApp === true;

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [sectionResults, setSectionResults] = useState({}); // { [sectionId]: results[] }
  const [completed, setCompleted] = useState(false);
  const [completedViaSibling, setCompletedViaSibling] = useState(null); // { title, language, slug }
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const saveTimerRef = useRef(null);

  // Build steps: description → intro → quiz → section_results per section, then conclusion
  const buildSteps = useCallback((c) => {
    if (!c) return [];
    const result = [];

    // Course description as first step (if it has content)
    if (c.description) {
      result.push({
        type: "description",
        title: c.title || "Welcome",
        subtitle: "Course overview",
      });
    }

    let questionNum = 1;
    (c.sections || []).forEach((section) => {
      if (section.intro_text) {
        result.push({
          type: "intro",
          title: section.title,
          subtitle: "Section introduction",
          section,
        });
      }
      if (section.questions && section.questions.length > 0) {
        const sectionStartNum = questionNum;
        result.push({
          type: "quiz",
          title: `Quiz: ${section.title}`,
          subtitle: `${section.questions.length} question${section.questions.length > 1 ? "s" : ""}`,
          section,
          startingNumber: sectionStartNum,
        });
        questionNum += section.questions.length;
        result.push({
          type: "section_results",
          title: section.title,
          subtitle: null,
          section,
          startingNumber: sectionStartNum,
        });
        // Add feedback page if section has open-ended questions
        const hasOpenEnded = section.questions.some(
          (q) => q.question_type === "open_ended"
        );
        if (hasOpenEnded) {
          result.push({
            type: "model_answers",
            title: section.title,
            subtitle: "Feedback",
            section,
            startingNumber: sectionStartNum,
          });
        }
      }
    });
    result.push({ type: "conclusion", title: c.conclusion_title || "Completed!", subtitle: "" });
    return result;
  }, []);

  const steps = useMemo(() => buildSteps(course), [course, buildSteps]);

  const saveProgress = useCallback(
    async (ans, step, options = {}) => {
      try {
        await updateEnrollment(slug, {
          answers: ans,
          last_step: step,
          ...(options.reset && { reset: true }),
        });
      } catch (err) {
        console.error("Save error:", err);
      }
    },
    [slug]
  );

  const debouncedSave = useCallback(
    (ans, step) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveProgress(ans, step), 1000);
    },
    [saveProgress]
  );

  // Fetch course + enrollment on mount
  useEffect(() => {
    const init = async () => {
      try {
        const { data: courseData } = await getCourse(slug);

        // Access check: non-admin users can only access courses in their language-filtered list
        if (!user?.is_admin) {
          try {
            const { data: allCourses } = await listCourses();
            const userLocale = localStorage.getItem("locale") || "en";
            const userLanguage = LOCALE_TO_LANGUAGE[userLocale] || "en";

            // Group courses by base_course_id and pick the one matching user's language
            const baseGroups = {};
            allCourses.forEach((c) => {
              const baseId = c.base_course_id || c.id;
              if (!baseGroups[baseId]) baseGroups[baseId] = [];
              baseGroups[baseId].push(c);
            });

            const allowedSlugs = new Set();
            Object.values(baseGroups).forEach((group) => {
              const match = group.find((c) => c.language === userLanguage);
              const selected = match || group.find((c) => c.language === "en") || group[0];
              if (selected) allowedSlugs.add(selected.slug);
            });

            if (!allowedSlugs.has(slug)) {
              navigate("/courses", { replace: true });
              return;
            }
          } catch {
            // If list fetch fails, allow access (BE still enforces sibling completion)
          }
        }

        setCourse(courseData);
        const stepsFromCourse = buildSteps(courseData);

        try {
          const { data: enrollment } = await getEnrollment(slug);

          // If completed via a sibling translation, show completion state
          if (enrollment?.completed_via_sibling) {
            setCompletedViaSibling({
              title: enrollment.sibling_course_title,
              language: enrollment.sibling_course_language,
              slug: enrollment.sibling_course_slug,
            });
            setLoading(false);
            return;
          }

          if (enrollment && enrollment.status) {
            const rawStep = enrollment.last_step ?? 0;
            const clampedStep = Math.min(rawStep, Math.max(0, stepsFromCourse.length - 1));
            const stepType = stepsFromCourse[clampedStep]?.type;

            if (enrollment.status === "completed") {
              setCompleted(true);
              setAnswers(enrollment.answers || {});
              setCurrentStep(Math.max(0, stepsFromCourse.length - 1));
            } else if (stepType === "section_results" || stepType === "model_answers") {
              // Stay on step and re-fetch results (including AI feedback) from API
              setAnswers(enrollment.answers || {});
              setCurrentStep(clampedStep);
              const section = stepsFromCourse[clampedStep]?.section;
              if (section) {
                const sectionAnswers = {};
                (section.questions || []).forEach((q) => {
                  if (enrollment.answers?.[q.id] !== undefined) {
                    sectionAnswers[q.id] = enrollment.answers[q.id];
                  }
                });
                try {
                  const { data } = await submitSection(slug, section.id, sectionAnswers);
                  setSectionResults((prev) => ({ ...prev, [section.id]: data.results }));
                } catch {
                  // If re-submit fails, fall back to quiz step
                  const quizIdx = stepsFromCourse.findIndex(
                    (s, i) => i < clampedStep && s.type === "quiz" && s.section.id === section.id
                  );
                  setCurrentStep(quizIdx >= 0 ? quizIdx : Math.max(0, clampedStep - 1));
                }
              }
            } else {
              setAnswers(enrollment.answers || {});
              setCurrentStep(clampedStep);
            }
          }
        } catch {
          // Not enrolled yet
        }
      } catch (err) {
        console.error("Failed to load course:", err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [slug, buildSteps, user, navigate]);

  const handleSelectAnswer = useCallback(
    (questionId, value) => {
      setValidationErrors((prev) => prev.filter((id) => id !== questionId));
      setShowValidationWarning(false);
      setAnswers((prev) => {
        const next = { ...prev, [questionId]: value };
        debouncedSave(next, currentStep);
        return next;
      });
    },
    [currentStep, debouncedSave]
  );

  // Validate all questions in current quiz step are answered
  const validateQuizStep = useCallback(() => {
    const step = steps[currentStep];
    if (step?.type !== "quiz") return true;

    const errors = [];
    (step.section.questions || []).forEach((q) => {
      const ans = answers[q.id];
      if (q.question_type === "open_ended") {
        if (typeof ans !== "string" || ans.trim().length === 0) errors.push(q.id);
      } else if (q.question_type === "multi_select") {
        if (!Array.isArray(ans) || ans.length === 0) errors.push(q.id);
      } else {
        if (ans === undefined || ans === null || ans === "") errors.push(q.id);
      }
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowValidationWarning(true);
      return false;
    }
    return true;
  }, [steps, currentStep, answers]);

  const canProceed = useCallback(() => {
    if (!steps[currentStep]) return false;
    const step = steps[currentStep];

    if (step.type === "description") return true;
    if (step.type === "intro") return true;
    if (step.type === "conclusion") return true;

    if (step.type === "quiz") {
      return (step.section.questions || []).every((q) => {
        const ans = answers[q.id];
        if (q.question_type === "open_ended")
          return typeof ans === "string" && ans.trim().length > 0;
        if (q.question_type === "multi_select") return Array.isArray(ans) && ans.length > 0;
        return ans !== undefined && ans !== null && ans !== "";
      });
    }

    if (step.type === "section_results") {
      const results = sectionResults[step.section.id];
      return results && results.every((r) => !r.graded || r.isCorrect);
    }

    return true;
  }, [steps, currentStep, answers, sectionResults]);

  const handleNext = useCallback(async () => {
    const step = steps[currentStep];

    // Validate quiz answers before submitting
    if (step?.type === "quiz") {
      if (!validateQuizStep()) return;
      if (submitting) return;

      setSubmitting(true);
      // Submit this section for grading
      try {
        const sectionAnswers = {};
        (step.section.questions || []).forEach((q) => {
          if (answers[q.id] !== undefined) {
            sectionAnswers[q.id] = answers[q.id];
          }
        });

        const { data } = await submitSection(slug, step.section.id, sectionAnswers);
        setSectionResults((prev) => ({ ...prev, [step.section.id]: data.results }));

        // If this is the last section and it passed, also call completeCourse for overall tracking
        const remainingSections = steps
          .filter((s, i) => i > currentStep && s.type === "quiz")
          .map((s) => s.section.id);

        if (data.passed && remainingSections.length === 0) {
          try {
            const completeRes = await completeCourse(slug, answers);
            if (completeRes.data.passed) setCompleted(true);
          } catch (err) {
            console.error("Complete error:", err);
          }
        }
      } catch (err) {
        console.error("Submit section error:", err);
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }

    // Moving from section_results: only allowed if passed
    if (step?.type === "section_results") {
      const results = sectionResults[step.section.id];
      if (!results || !results.every((r) => !r.graded || r.isCorrect)) return;
    }

    const nextStep = Math.min(currentStep + 1, steps.length - 1);
    setCurrentStep(nextStep);
    setValidationErrors([]);
    setShowValidationWarning(false);
    debouncedSave(answers, nextStep);
  }, [currentStep, steps, slug, answers, sectionResults, debouncedSave, validateQuizStep, submitting]);

  // Find the lowest step the user can navigate back to (cannot cross section_results boundaries)
  const backLimit = useMemo(() => {
    for (let i = currentStep - 1; i >= 0; i--) {
      if (steps[i]?.type === "section_results") return i + 1;
    }
    return 0;
  }, [currentStep, steps]);

  const handlePrevious = useCallback(() => {
    const prev = currentStep - 1;
    if (prev < backLimit) return;

    setCurrentStep(prev);
    setValidationErrors([]);
    setShowValidationWarning(false);
    saveProgress(answers, prev);
  }, [currentStep, backLimit, answers, saveProgress]);

  const handleRetakeSection = useCallback(
    (sectionId) => {
      // Find the quiz step for this section
      const quizIdx = steps.findIndex((s) => s.type === "quiz" && s.section.id === sectionId);
      if (quizIdx >= 0) {
        // Clear answers for this section only
        const sectionQuestionIds = steps[quizIdx].section.questions.map((q) => q.id);
        setAnswers((prev) => {
          const next = { ...prev };
          sectionQuestionIds.forEach((id) => delete next[id]);
          saveProgress(next, quizIdx, { reset: true });
          return next;
        });
        setSectionResults((prev) => {
          const next = { ...prev };
          delete next[sectionId];
          return next;
        });
        setCurrentStep(quizIdx);
        setValidationErrors([]);
        setShowValidationWarning(false);
      }
    },
    [steps, saveProgress]
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Enter" && !e.shiftKey && e.target.tagName !== "TEXTAREA") {
        e.preventDefault();
        handleNext();
      } else if (
        e.key === "Backspace" &&
        e.target.tagName !== "INPUT" &&
        e.target.tagName !== "TEXTAREA"
      ) {
        e.preventDefault();
        handlePrevious();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleNext, handlePrevious]);

  if (loading) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-white">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
          <motion.img
            src={juniorDuck}
            alt="Loading..."
            className="tw-h-28 tw-w-24"
            animate={{ y: [0, -16, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          />
          <p className="tw-text-sm tw-text-gray-500">Loading course...</p>
        </div>
      </div>
    );
  }

  const handleGoBack = () => {
    if (isInApp && window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: completed ? "courseCompleted" : "goBack",
          courseSlug: slug,
        })
      );
    } else {
      navigate("/courses");
    }
  };

  if (!course) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-white">
        <div className="tw-text-center">
          <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900">Course not found</h2>
          <button
            onClick={handleGoBack}
            className="tw-mt-4 tw-text-primary-main hover:tw-underline"
          >
            {isInApp ? "Go back" : "Back to courses"}
          </button>
        </div>
      </div>
    );
  }

  if (completedViaSibling) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-white">
        <div className="tw-w-full tw-max-w-lg tw-space-y-6 tw-px-4 tw-text-center">
          <div className="tw-flex tw-justify-center">
            <div className="tw-flex tw-h-24 tw-w-24 tw-items-center tw-justify-center tw-rounded-full tw-bg-green-100">
              <Check className="tw-h-12 tw-w-12 tw-text-green-600" />
            </div>
          </div>
          <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900">Already completed</h2>
          <p className="tw-text-gray-500">
            You have already completed this course in another language (
            {completedViaSibling.language}): <strong>{completedViaSibling.title}</strong>. You do
            not need to take it again.
          </p>
          <button
            onClick={handleGoBack}
            className="tw-rounded-full tw-bg-primary-main tw-px-8 tw-py-3 tw-font-medium tw-text-white tw-transition-all hover:tw-bg-primary-main/90"
          >
            {isInApp ? "Done" : "Back to courses"}
          </button>
        </div>
      </div>
    );
  }

  const currentStepData = steps[currentStep];

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-gray-900">
      {/* Back navigation - hidden in app WebView */}
      {!isInApp && (
        <div className="tw-fixed tw-left-4 tw-top-4 tw-z-50 md:tw-left-8 md:tw-top-8">
          <button
            onClick={() => navigate("/courses")}
            className="tw-group tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-gray-100 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-transition-all hover:tw-bg-gray-200 hover:tw-text-gray-900"
          >
            <ArrowLeft className="tw-h-4 tw-w-4 tw-transition-transform group-hover:-tw-translate-x-1" />
            Back to courses
          </button>
        </div>
      )}

      {/* Progress bar */}
      <div className="tw-fixed tw-left-0 tw-right-0 tw-top-0 tw-z-40 tw-h-1 tw-bg-gray-100">
        <motion.div
          className="tw-h-full tw-bg-primary-main"
          initial={{ width: "0%" }}
          animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Main content */}
      <div className="tw-container tw-mx-auto tw-min-h-screen tw-max-w-4xl tw-px-5 tw-pb-36 tw-pt-20 md:tw-px-8 md:tw-pb-40">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="tw-flex tw-min-h-[60vh] tw-flex-col tw-items-center"
          >
            {/* Step title */}
            {currentStepData && (
              <div className="tw-mb-8 tw-text-center md:tw-mb-12">
                <h1 className="tw-mb-3 tw-text-2xl tw-font-bold md:tw-text-4xl">
                  {currentStepData.title}
                </h1>
                {currentStepData.subtitle && (
                  <p className="tw-text-lg tw-text-gray-500">{currentStepData.subtitle}</p>
                )}
              </div>
            )}

            {/* Step content */}
            {currentStepData?.type === "description" && (
              <div className="tw-w-full tw-max-w-2xl">
                <RichTextViewer content={course.description} className="tw-text-gray-600" />
              </div>
            )}

            {currentStepData?.type === "intro" && (
              <SectionIntro
                title={currentStepData.section.title}
                introText={currentStepData.section.intro_text}
              />
            )}

            {currentStepData?.type === "quiz" && (
              <>
                <QuizQuestion
                  questions={currentStepData.section.questions}
                  selectedAnswers={answers}
                  onSelect={handleSelectAnswer}
                  startingNumber={currentStepData.startingNumber}
                  validationErrors={validationErrors}
                />
                {showValidationWarning && (
                  <ValidationWarning message="Please answer all questions before submitting." />
                )}
              </>
            )}

            {currentStepData?.type === "section_results" &&
              sectionResults[currentStepData.section.id] && (
                <QuizFeedback
                  questions={currentStepData.section.questions}
                  answers={answers}
                  sectionResults={sectionResults[currentStepData.section.id]}
                  startingNumber={currentStepData.startingNumber}
                  onRetake={() => handleRetakeSection(currentStepData.section.id)}
                />
              )}

            {currentStepData?.type === "model_answers" && (
              <ModelAnswersView
                questions={currentStepData.section.questions}
                answers={answers}
                startingNumber={currentStepData.startingNumber}
                sectionResults={sectionResults[currentStepData.section.id]}
              />
            )}

            {currentStepData?.type === "conclusion" && (
              <SuccessView
                conclusionTitle={course.conclusion_title}
                conclusionText={course.conclusion_text}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Bottom navigation */}
        <div className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-border-t tw-border-gray-200 tw-bg-white/95 tw-backdrop-blur-sm">
          <div className="tw-container tw-mx-auto tw-flex tw-max-w-4xl tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 md:tw-px-6 md:tw-py-4">
            <button
              onClick={handlePrevious}
              className={`tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-px-4 tw-py-2.5 tw-text-sm tw-transition-all md:tw-gap-2 md:tw-px-6 md:tw-py-3 md:tw-text-base ${
                currentStep <= backLimit
                  ? "tw-pointer-events-none tw-opacity-0"
                  : "tw-opacity-100 hover:tw-bg-gray-100"
              }`}
            >
              <ArrowLeft className="tw-h-4 tw-w-4 md:tw-h-5 md:tw-w-5" />
              Back
            </button>

            <div className="tw-shrink-0 tw-text-xs tw-text-gray-400 md:tw-text-sm md:tw-text-gray-500">
              {currentStep + 1} / {steps.length}
            </div>

            {currentStep === steps.length - 1 ? (
              <button
                onClick={() => {
                  if (isInApp && window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(
                      JSON.stringify({ type: "courseCompleted", courseSlug: slug })
                    );
                  } else {
                    navigate("/courses");
                  }
                }}
                className="tw-shrink-0 tw-whitespace-nowrap tw-rounded-full tw-bg-primary-main tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary-main/90 md:tw-px-8 md:tw-py-3 md:tw-text-base"
              >
                {isInApp ? "Done" : "Back to courses"}
              </button>
            ) : currentStepData?.type === "section_results" ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-transition-all md:tw-gap-2 md:tw-px-8 md:tw-py-3 md:tw-text-base ${
                  canProceed()
                    ? "tw-bg-primary-main tw-text-white hover:tw-bg-primary-main/90"
                    : "tw-cursor-not-allowed tw-bg-gray-100 tw-text-gray-400"
                }`}
              >
                {steps[currentStep + 1]?.type === "model_answers" ? "View feedback" : "Next"}
                <ArrowRight className="tw-h-4 tw-w-4 md:tw-h-5 md:tw-w-5" />
              </button>
            ) : currentStepData?.type === "model_answers" ? (
              <button
                onClick={handleNext}
                className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-bg-primary-main tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-transition-all hover:tw-bg-primary-main/90 md:tw-gap-2 md:tw-px-8 md:tw-py-3 md:tw-text-base"
              >
                Continue
                <ArrowRight className="tw-h-4 tw-w-4 md:tw-h-5 md:tw-w-5" />
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={submitting}
                className={`tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-transition-all md:tw-gap-2 md:tw-px-8 md:tw-py-3 md:tw-text-base ${
                  submitting
                    ? "tw-cursor-not-allowed tw-bg-primary-main/60 tw-text-white"
                    : currentStepData?.type === "quiz"
                      ? "tw-bg-primary-main tw-text-white hover:tw-bg-primary-main/90"
                      : canProceed()
                        ? "tw-bg-primary-main tw-text-white hover:tw-bg-primary-main/90"
                        : "tw-cursor-not-allowed tw-bg-gray-100 tw-text-gray-400"
                }`}
              >
                {submitting ? (
                  <>
                    Submitting
                    <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin md:tw-h-5 md:tw-w-5" />
                  </>
                ) : (
                  <>
                    {currentStepData?.type === "quiz" ? "Submit" : "Continue"}
                    <ArrowRight className="tw-h-4 tw-w-4 md:tw-h-5 md:tw-w-5" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseTaker;
